import React, { useEffect } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store";
import { routes } from "routes";

import { useResource } from "hooks/useResource";
import { getInternationalDateFormat } from "utils/getInternationalDateFormat";
import { getDayMonthYearFromDate } from "utils/formatters";
import { fetchModelLicenseList, syncModels } from "store/licenseManagement/thunks";
import { clearFilters, updateFilters } from "store/licenseManagement/modelManagementSlice";

import { ModelToggleSwitch } from "./ModelToggleSwitch";
import { FlexTable } from "components/FlexTable";
import { Button } from "components/_buttons/Button";
import { Icon, IconType } from "components/_icons/Icon";
import { TableFilterDropdown } from "components/TableFilterDropdown";
import { Text } from "components/Text";
import { CategorizationTypeInfo } from "components/CategorizationTypeInfo";

import { ButtonSize, ButtonVariant } from "ts/enums/button";
import { Color } from "ts/enums/color";
import { ZIndexStackingContext } from "ts/enums/zIndexStackingContext";
import { RoleType } from "@explorance/mly-types";
import { FlexTableType } from "ts/enums/flexTable";

const TABLE_HEADERS = [
  "name",
  "version",
  "env",
  "creationDate",
  "lastUpdated",
  "deprecatesOn",
  "status",
];

export const ModelManagementPage = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const state = useAppSelector((state) => state.modelManagement);
  const dispatch = useAppDispatch();

  const { getResource } = useResource();
  const { push } = useHistory();

  const navigateBackToLicensing = () => push(routes.licensingPage);

  useEffect(() => {
    dispatch(fetchModelLicenseList());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser.roleType !== RoleType.SuperAdmin) push(routes.homePage);
  }, [currentUser, push]);

  return (
    <div className="fade-in">
      <StyledPageTop>
        <Button
          variant={ButtonVariant.secondary}
          size={ButtonSize.md}
          onClick={navigateBackToLicensing}
        >
          <StyledButtonContent>
            <Icon type={IconType.chevronLeft} size={14} />
            <span>
              <Text resource="modelManagement.button.backToLicensing" />
            </span>
          </StyledButtonContent>
        </Button>
        <StyledPageHeader>
          <h1>
            <Text resource="modelManagement.title" />
          </h1>
          <Button
            variant={ButtonVariant.secondary}
            size={ButtonSize.md}
            onClick={() => dispatch(syncModels())}
            disabled={state.isSynchronizing}
          >
            <StyledButtonContent>
              <Icon type={IconType.refresh} size={14} />
              <span>
                <Text resource="licensing.button.synchronize" />
              </span>
            </StyledButtonContent>
          </Button>
        </StyledPageHeader>
      </StyledPageTop>
      <TableFilterDropdown
        currentFilters={state.currentEnvFilters}
        allFilters={state.allEnvFilters}
        updateFilters={(env) => dispatch(updateFilters(env))}
        deleteFilters={() => dispatch(clearFilters())}
        nameKey={"button.filter.byEnv"}
        customStyles={{
          zIndex: ZIndexStackingContext.medium,
        }}
      />
      <FlexTable
        type={FlexTableType.Table}
        data={{
          headers: TABLE_HEADERS.map((header) => getResource(`licensing.table.${header}`)),
          rows: state.filteredModelLicenses?.map((model) => ({
            contentId: model.modelId,
            data: [
              <StyledNameSection key={model.modelId}>
                {model.family && <CategorizationTypeInfo family={model.family} />}
                <div>
                  <b>
                    <Text resource="licensing.table.originalName" />:
                  </b>
                  {model.name}
                </div>
                <div>
                  <b>
                    <Text resource="licensing.table.description" />:
                  </b>
                  {model.description}
                </div>
              </StyledNameSection>,
              <span key={model.modelId}>{model.version}</span>,
              <span key={model.modelId}>{model?.env}</span>,
              <span key={model.modelId}>
                {getInternationalDateFormat(getDayMonthYearFromDate(model.createdDate.toString()))}
              </span>,
              <span key={model.modelId}>
                {getInternationalDateFormat(getDayMonthYearFromDate(model.lastUpdated.toString()))}
              </span>,
              <span key={model.modelId}>
                {model.deprecatesOn &&
                  getInternationalDateFormat(
                    getDayMonthYearFromDate(model.deprecatesOn.toString())
                  )}
              </span>,
              <ModelToggleSwitch model={model} key={model.modelId} />,
            ],
          })),
          columnWidths: ["32%", "8%", "11%", "12.5%", "12.5%", "12.5%", "11.5%"],
        }}
        isLoading={state.modelLicenses?.length === 0}
        disableSorting={true}
        customStyles={{
          rows: {
            padding: "10px",
            backgroundColor: Color.white,
            rowHeight: "250px",
          },
        }}
      />
    </div>
  );
};

const StyledPageTop = styled.div`
  position: sticky;
  top: 0;
  padding: 24px 0 20px 0;
  background-color: ${Color.white};
  z-index: ${ZIndexStackingContext.low};

  h1 {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }
`;

const StyledPageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 13px;
`;

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledNameSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 95%;
`;
